import { useEffect } from 'react';
import { useState } from 'react';

import {
  MapboxOverlay as DeckOverlay,
  MapboxOverlayProps,
} from '@deck.gl/mapbox';
import { useWindowSize } from '@uidotdev/usehooks';
import { scaleLinear, scaleLog } from 'd3-scale';
import {
  AmbientLight,
  Color,
  HexagonLayer,
  LightingEffect,
  PickingInfo,
  PointLight,
} from 'deck.gl';
import { debounce } from 'lodash';
import { ProjectionSpecification } from 'mapbox-gl';
import {
  Map,
  NavigationControl,
  ScaleControl,
  ViewStateChangeEvent,
  useControl,
} from 'react-map-gl';
import { useSnapshot } from 'valtio';

import { ThemeProvider } from '@mui/material/styles';

import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';

import { denseTheme, desktopTheme } from '@/theme';

import {
  getBookingsExpandedQueryWhereClauseFromFilters,
  store,
} from '@/components/pages/EmissionsDashboard/store';

import { useGetTemperatureBreachesQuery } from '@/graphql/GetTemperatureBreaches.generated';
import { useGetTemperatureBreachesExpandedQuery } from '@/graphql/GetTemperatureBreachesExpanded.generated';
import {
  Container_Insights_Temperature_Breaches,
  Container_Insights_Temperature_Breaches_Constraint,
  Container_Insights_Temperature_Breaches_Expanded,
} from '@/graphql/types';

import { useBreakpoints } from '@/hooks';

import { reportRequestError, retryAfterRequestError } from '@/store';
import { appStore } from '@/store';

import { FiltersPanel } from './Filters';
import { MapTooltip } from './MapTooltip';
import { TripList } from './TripList';
import './mapbox-overrides.css';

type DataPoint = [longitude: number, latitude: number];

type TemperatureBreachEvent = {
  coordinates: [lng: number, lat: number];
  max_deviation: number;
};

const ambientLight = new AmbientLight({
  color: [255, 255, 255],
  intensity: 1.0,
});

const pointLight1 = new PointLight({
  color: [255, 255, 255],
  intensity: 0.8,
  position: [-0.144528, 49.739968, 80000],
});

const pointLight2 = new PointLight({
  color: [255, 255, 255],
  intensity: 0.8,
  position: [-3.807751, 54.104682, 8000],
});

const colorRange: Color[] = [
  [1, 152, 189], // rgb(1, 152, 189)
  [73, 227, 206], // rgb(73, 227, 206)
  [216, 254, 181], // rgb(216, 254, 181)
  [254, 237, 177], //rgb(254,237,177)
  [254, 173, 84], //rgb(254,173,84)
  [209, 55, 78], //rgb(209,55,78)
];

const lightingEffect = new LightingEffect({
  ambientLight,
  pointLight1,
  pointLight2,
});

const {
  VITE_MAPBOX_TOKEN,
  VITE_MAPBOX_PROJECTION,
  VITE_MAPBOX_DEFAULT_ZOOM,
  VITE_MAPBOX_DEFAULT_LATITUDE,
  VITE_MAPBOX_DEFAULT_LONGITUDE,
  VITE_MAPBOX_HEATMAP_STYLE,
  VITE_MAPBOX_STYLE,
} = import.meta.env;

function DeckGLOverlay(props: MapboxOverlayProps) {
  const overlay = useControl(() => new DeckOverlay(props) as any);
  overlay.setProps(props);
  return null;
}

export type ContainerInsightsTemperatureBreaches = Omit<
  Container_Insights_Temperature_Breaches,
  'id' | 'telematic_device' | 'telematic_device_id'
>;

export type ContainerInsightsTemperatureBreachesExpanded = Omit<
  Container_Insights_Temperature_Breaches_Expanded,
  'telematic_device' | 'telematic_device_id'
>;

function Heatmap() {
  const windowSize = useWindowSize();
  const appSnap = useSnapshot(appStore);
  const { isMobileDevice } = useBreakpoints();
  const width = windowSize.width || 0;
  const height = windowSize.height
    ? windowSize.height - appSnap.appBar.height
    : 0;

  const snap = useSnapshot(store) as typeof store;

  const whereBookings = getBookingsExpandedQueryWhereClauseFromFilters(
    snap as typeof store,
  );

  const [{ data, error, fetching }, reexecuteQuery] =
    useGetTemperatureBreachesExpandedQuery({
      variables: {
        where: {
          breach_start_datetime: {
            _gte: snap.daterange.from.toISOString(),
          },
          breach_end_datetime: {
            _lte: snap.daterange.to.toISOString(),
          },
          telematic_device: {
            bookings_expanded: {
              ...whereBookings,
            },
          },
        },
      },
    });

  const [dataset, setDataset] = useState<
    ContainerInsightsTemperatureBreachesExpanded[]
  >([]);
  useEffect(() => {
    if (data) {
      setDataset(
        data.container_insights_temperature_breaches_expanded.filter(
          (d) => d.breach_start_gps !== null,
        ),
      );
    }
  }, [data]);

  useEffect(() => {
    if (error) {
      reportRequestError(error, reexecuteQuery);
    }
  }, [error]);

  const [popupInfo, setPopupInfo] = useState<PickingInfo | null>(null);
  const [holdPopup, setHoldPopup] = useState(false);

  const [currentZoom, setCurrentZoom] = useState(
    parseInt(VITE_MAPBOX_DEFAULT_ZOOM),
  );
  const handleZoom = debounce((event: ViewStateChangeEvent) => {
    setCurrentZoom(event.viewState.zoom);
  }, 250);

  const scaleCoverage = scaleLinear()
    .domain([1, 2, 3, 4, 5, 6, 7, 8, 9, 10])
    .range([20, 15, 10, 10, 5, 5, 2, 2, 2, 1])
    .clamp(true);

  const scaleRadius = scaleLinear()
    .domain([1, 2, 3, 4, 5, 6, 7, 8, 9, 10])
    .range([5000, 5000, 4000, 4000, 3000, 3000, 2000, 1000, 1000, 1000]);

  const radius = scaleRadius(currentZoom);
  // hexagon radis multiplier (only for display: hexagon radius = coverage*radius)
  const coverage = scaleCoverage(currentZoom);

  const layers = [
    new HexagonLayer<Container_Insights_Temperature_Breaches_Expanded>({
      id: 'heatmap',
      colorRange,
      coverage,
      data: dataset,
      elevationRange: [0, 1000],
      elevationScale: 1000,
      extruded: true,
      getPosition: (d) => d.breach_start_gps.coordinates,
      pickable: true,
      radius,
      material: {
        ambient: 0.64,
        diffuse: 0.6,
        shininess: 32,
        specularColor: [51, 51, 51],
      },
      transitions: {
        coverage: 60,
      },

      onHover: (info) => {
        if (!holdPopup) {
          setPopupInfo(info);
        }
        return true;
      },
      onClick: () => {
        setHoldPopup(!holdPopup);
        return true;
      },
    }),
  ];

  return (
    <ThemeProvider theme={isMobileDevice ? denseTheme : desktopTheme}>
      <Box
        sx={{
          bgcolor: '#191919',
        }}
      >
        {fetching && <LinearProgress />}
        <Map
          mapboxAccessToken={VITE_MAPBOX_TOKEN}
          mapStyle={VITE_MAPBOX_HEATMAP_STYLE}
          projection={{
            name: VITE_MAPBOX_PROJECTION as ProjectionSpecification['name'],
          }}
          initialViewState={{
            longitude: parseFloat(VITE_MAPBOX_DEFAULT_LONGITUDE),
            latitude: parseFloat(VITE_MAPBOX_DEFAULT_LATITUDE),
            zoom: parseInt(VITE_MAPBOX_DEFAULT_ZOOM),
          }}
          reuseMaps
          style={{
            width,
            height,
          }}
          pitch={30}
          onZoom={handleZoom}
        >
          <DeckGLOverlay
            layers={layers}
            interleaved={true}
            pickingRadius={8}
            effects={[lightingEffect]}
          ></DeckGLOverlay>

          {popupInfo && (
            <MapTooltip
              popup={popupInfo}
              elevation={holdPopup ? 8 : 1}
              onClose={() => {
                setPopupInfo(null);
                setHoldPopup(false);
              }}
            />
          )}

          <NavigationControl position="top-right" />
          <ScaleControl position="bottom-right" />

          <FiltersPanel />
          <TripList />
        </Map>
      </Box>
    </ThemeProvider>
  );
}

export { Heatmap };
